import { useMemo, useState } from 'react';
import type { ColumnDef, EntityDef } from '@/config/masterData';
import { useLookups, useRows } from '@/hooks/useLive';
import { save, softDelete, upsert } from '@/lib/crud';
import { num } from '@/lib/format';
import { useAuthStore } from '@/stores/authStore';
import { roleLock } from '@/lib/editability';
import { Panel } from '@/components/ui/Panel';
import { Button } from '@/components/ui/Button';
import { ConfirmRemove } from '@/components/ui/ConfirmRemove';
import { Drawer } from '@/components/ui/Drawer';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { StatusChip } from '@/components/ui/StatusChip';
import { EntityForm } from './EntityForm';
import { emptyValues, validate } from './formValues';

type Row = Record<string, unknown>;

export function EntityManager({ entity, canEdit: canEditOverride }: {
  entity: EntityDef;
  /** For screens outside Administration whose rule is not a quarry role
   *  (the concrete lists, kept by branch managers too). */
  canEdit?: boolean;
}) {
  const all = useRows<Row>(entity.table, entity.orderBy);
  // A list sharing its table with another (plant products, 0063) shows its own rows.
  const rows = useMemo(() => (entity.fixed
    ? all?.filter((r) => Object.entries(entity.fixed!).every(([k, v]) => r[k] === v))
    : all), [all, entity.fixed]);
  const hasRole = useAuthStore((s) => s.hasRole);
  const canEdit = canEditOverride ?? hasRole(...entity.editableBy);
  const role = useAuthStore((s) => s.profile?.role);
  // Rows open for everyone; a person who may not change them is told so
  // inside, rather than a click that does nothing (0046).
  const lock = canEdit ? null
    : canEditOverride === undefined
      ? roleLock(role, entity.editableBy, entity.label.toLowerCase())
      : `You can view ${entity.label.toLowerCase()} but not change them. Ask a manager to make the change.`;

  const [editing, setEditing] = useState<Row | null>(null);
  const [values, setValues] = useState<Row>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  // Lookup maps for foreign-key columns, resolved from the local mirror.
  const lookupDefs = useMemo(
    () => entity.columns.filter((c) => c.lookup).map((c) => c.lookup!),
    [entity],
  );
  const lookups = useLookups(lookupDefs);

  function openNew() {
    setValues(emptyValues(entity.fields));
    setErrors({});
    setEditing({});
  }

  function openEdit(row: Row) {
    const v: Row = {};
    for (const f of entity.fields) v[f.name] = row[f.name] ?? null;
    setValues(v);
    setErrors({});
    setEditing(row);
  }

  async function onSave() {
    const errs = validate(entity.fields, values);
    setErrors(errs);
    if (Object.keys(errs).length) return;

    setBusy(true);
    const payload: Row = { ...values, ...(entity.fixed ?? {}) };
    if (editing?.id) payload.id = editing.id;

    const result = await save(
      () => upsert(entity.table, payload as Row & { id?: string }),
      `${entity.singular} saved`,
    );
    setBusy(false);
    if (result) setEditing(null);
  }

  async function onDelete(): Promise<boolean> {
    if (!editing?.id) return false;
    setBusy(true);
    const ok = await save(
      () => softDelete(entity.table, String(editing.id)),
      `${entity.singular} removed`,
    );
    setBusy(false);
    if (ok !== null) setEditing(null);
    return ok !== null;
  }

  const columns: Column<Row>[] = entity.columns.map((c) => ({
    key: c.key,
    label: c.label,
    align: c.align,
    value: (row) => renderValue(row, c, lookups) as string,
    render: (row) => renderCell(row, c, lookups),
  }));

  return (
    <>
      <Panel
        title={entity.label}
        flush
        actions={
          canEdit ? (
            <Button style={{ minHeight: 32 }} onClick={openNew}>+ New</Button>
          ) : (
            <span className="small muted">Read only for your role</span>
          )
        }
      >
        <div style={{ padding: 'var(--sp-3) var(--sp-3) 0' }} className="small muted">
          {entity.description}
        </div>
        <DataTable
          rows={rows}
          columns={columns}
          rowKey={(r) => String(r.id)}
          onRowClick={openEdit}
          defaultSort={entity.orderBy}
          searchPlaceholder={`Search ${entity.label.toLowerCase()}…`}
          empty={`No ${entity.label.toLowerCase()} yet.`}
        />
      </Panel>

      <Drawer
        open={editing !== null}
        title={editing?.id ? `Edit ${entity.singular.toLowerCase()}` : `New ${entity.singular.toLowerCase()}`}
        subtitle={editing?.id ? String(editing.name ?? editing.code ?? '') : entity.description}
        onClose={() => setEditing(null)}
        locked={lock}
        footer={
          <>
            {editing?.id && (
              <ConfirmRemove onConfirm={onDelete} disabled={busy}
                question={`Remove this ${entity.singular.toLowerCase()}?`} />
            )}
            <span className="spacer" />
            <Button variant="secondary" onClick={() => setEditing(null)} disabled={busy}>Cancel</Button>
            <Button onClick={onSave} loading={busy}>Save</Button>
          </>
        }
      >
        <EntityForm
          fields={entity.fields}
          values={values}
          errors={errors}
          onChange={(name, value) => setValues((v) => ({ ...v, [name]: value }))}
        />
      </Drawer>
    </>
  );
}

/* ------------------------------------------------------------------ */

type Lookups = Record<string, Map<string, string>>;

function renderValue(row: Row, c: ColumnDef, lookups: Lookups): string | number | null {
  const raw = row[c.key];
  if (c.lookup) return lookups[c.lookup.table]?.get(String(raw)) ?? '';
  if (raw === null || raw === undefined) return null;
  if (c.type === 'number') return Number(raw);
  if (c.type === 'boolean') return raw ? 'yes' : 'no';
  return String(raw);
}

function renderCell(row: Row, c: ColumnDef, lookups: Lookups) {
  const raw = row[c.key];

  if (c.type === 'boolean') {
    return raw ? <StatusChip tone="green" dot={false}>Yes</StatusChip>
               : <span className="muted">—</span>;
  }
  if (c.type === 'colour') {
    return raw ? (
      <span style={{
        display: 'inline-block', width: 16, height: 16, borderRadius: 3,
        background: String(raw), border: '1px solid var(--border-medium)',
      }} title={String(raw)} />
    ) : <span className="muted">—</span>;
  }
  if (c.type === 'number') {
    return <span className="tabular">{num(raw as number, c.decimals ?? 0)}</span>;
  }
  if (c.lookup) {
    return lookups[c.lookup.table]?.get(String(raw)) || <span className="muted">—</span>;
  }
  return raw === null || raw === undefined || raw === '' ? <span className="muted">—</span> : String(raw);
}
